import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Save,
  CalendarPlus,
  CalendarClock,
  Trash2,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: "mot_expiry",         label: "MOT expiry",          kind: "mot" },
  { key: "insurance_renewal",  label: "Insurance renewal",   kind: "insurance" },
  { key: "tax_renewal",        label: "Vehicle tax renewal", kind: "tax" },
  { key: "next_service",       label: "Next service",        kind: "service" },
];

const PLACEMENT_OPTIONS = [
  { v: "before_work", l: "Before work starts" },
  { v: "travel_gap",  l: "Use travel gap" },
  { v: "lunch_break", l: "Use lunch break" },
  { v: "end_of_day",  l: "End of work day" },
  { v: "ask",         l: "Ask each time" },
  { v: "auto",        l: "Auto choose best slot" },
];

const DURATIONS = [15, 30, 45, 60];

/**
 * CompliancePage — set compliance dates + manage scheduled tasks.
 * Auto-generates pending tasks from configured dates within reminder lead window.
 */
export default function CompliancePage() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [comp, setComp] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([
        axios.get(`${API}/api/compliance/me`, { withCredentials: true }),
        axios.get(`${API}/api/compliance/tasks`, { withCredentials: true }),
      ]);
      setComp(c.data.compliance);
      setTasks(t.data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setComp((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/compliance/me`, comp || {}, { withCredentials: true });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const autoGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/compliance/auto-generate`, {}, { withCredentials: true });
      if (data.count > 0) toast.success(`Generated ${data.count} task${data.count === 1 ? "" : "s"}`);
      else toast.message("No new tasks — already up to date");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate");
    } finally {
      setGenerating(false);
    }
  };

  const schedule = async (taskId) => {
    try {
      const { data } = await axios.post(`${API}/api/compliance/tasks/${taskId}/schedule`, {}, { withCredentials: true });
      toast.success(`Scheduled ${data.task.scheduled_date} at ${data.task.scheduled_time}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't schedule");
    }
  };

  const removeTask = async (taskId) => {
    if (!window.confirm("Remove this task and unblock the time?")) return;
    try {
      await axios.delete(`${API}/api/compliance/tasks/${taskId}`, { withCredentials: true });
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  };

  const completeTask = async (taskId) => {
    try {
      await axios.post(`${API}/api/compliance/tasks/${taskId}/complete`, {}, { withCredentials: true });
      toast.success("Marked complete");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update");
    }
  };

  if (loading || !comp) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-950"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white pb-24">
      <header className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="comp-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400">Compliance</p>
            <p className="text-[11px] text-slate-400 truncate">Stay legal. Stay earning.</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Compliance dates */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400 mb-3">Renewal dates</p>
          <div className="space-y-3">
            {FIELDS.map((f) => (
              <DateField key={f.key} label={f.label} value={comp[f.key] || ""} onChange={(v) => setField(f.key, v)} testid={`comp-date-${f.kind}`} />
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-800 grid grid-cols-2 gap-3">
            <DateField
              label="Last tyre check"
              value={comp.last_tyre_check || ""}
              onChange={(v) => setField("last_tyre_check", v)}
              testid="comp-date-tyre-last"
            />
            <NumberField
              label="Tyre cycle (days)"
              value={comp.tyre_reminder_cycle_days || ""}
              onChange={(v) => setField("tyre_reminder_cycle_days", v ? parseInt(v, 10) : null)}
              placeholder="e.g. 30"
              min={14}
              max={365}
              testid="comp-tyre-cycle"
            />
            <NumberField
              label="Reminder lead (days)"
              value={comp.reminder_lead_days ?? 30}
              onChange={(v) => setField("reminder_lead_days", parseInt(v || "30", 10))}
              min={1}
              max={120}
              testid="comp-lead"
            />
          </div>
        </div>

        {/* Defaults */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400 mb-3">Smart-time defaults</p>
          <Field label="When to slot tasks">
            <select
              data-testid="comp-default-placement"
              value={comp.default_placement || "auto"}
              onChange={(e) => setField("default_placement", e.target.value)}
              className={fieldCls()}
            >
              {PLACEMENT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
          <Field label="Default duration">
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  data-testid={`comp-default-dur-${d}`}
                  onClick={() => setField("default_duration_minutes", d)}
                  className={`flex-1 py-2 rounded-xl text-[11px] font-extrabold ${
                    (comp.default_duration_minutes || 30) === d
                      ? "bg-violet-600 text-white"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                >{d} min</button>
              ))}
            </div>
          </Field>
          <button
            data-testid="comp-save"
            onClick={save}
            disabled={saving}
            className="mt-4 w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>

        {/* Tasks */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400">Scheduled tasks</p>
              <p className="text-[11px] text-slate-400">Auto-placed in your diary, blocked from pupil bookings.</p>
            </div>
            <button
              data-testid="comp-auto-generate"
              onClick={autoGenerate}
              disabled={generating}
              className="px-3 py-1.5 rounded-full text-[11px] font-extrabold bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-60 inline-flex items-center gap-1"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Generate
            </button>
          </div>
          {tasks.length === 0 ? (
            <p className="text-[12px] text-slate-500 text-center py-4">
              No tasks yet. Add your renewal dates above and tap Generate.
            </p>
          ) : (
            <ul data-testid="comp-tasks" className="space-y-2">
              {tasks.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  onSchedule={() => schedule(t.id)}
                  onRemove={() => removeTask(t.id)}
                  onComplete={() => completeTask(t.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskRow({ task, onSchedule, onRemove, onComplete }) {
  const tone =
    task.status === "completed" ? "bg-emerald-600/15 border-emerald-600/40" :
    task.status === "scheduled" ? "bg-violet-600/15 border-violet-500/40" :
    "bg-slate-800 border-slate-700";
  return (
    <li data-testid={`comp-task-${task.id}`} className={`rounded-xl border px-3 py-3 ${tone}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-extrabold truncate">{task.title}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Due {task.due_date} · {task.duration_minutes} min · {task.placement_preference}
          </p>
          {task.scheduled_date && (
            <p className="text-[11px] text-violet-300 mt-1 inline-flex items-center gap-1">
              <CalendarClock className="w-3 h-3" />
              Scheduled {task.scheduled_date} at {task.scheduled_time}
            </p>
          )}
        </div>
        {task.status !== "completed" && (
          <>
            {task.status !== "scheduled" && (
              <button
                data-testid={`comp-task-schedule-${task.id}`}
                onClick={onSchedule}
                className="p-2 rounded-lg text-violet-300 hover:bg-violet-500/15"
                title="Auto-schedule"
              >
                <CalendarPlus className="w-4 h-4" />
              </button>
            )}
            <button
              data-testid={`comp-task-complete-${task.id}`}
              onClick={onComplete}
              className="p-2 rounded-lg text-emerald-300 hover:bg-emerald-500/15"
              title="Mark complete"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </>
        )}
        <button
          data-testid={`comp-task-remove-${task.id}`}
          onClick={onRemove}
          className="p-2 rounded-lg text-rose-300 hover:bg-rose-500/15"
          title="Remove"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="text-[11px] font-bold text-slate-300">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={fieldCls()}
      />
    </Field>
  );
}

function NumberField({ label, value, onChange, placeholder, min, max, testid }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={fieldCls()}
      />
    </Field>
  );
}

function fieldCls() {
  return "w-full px-3 py-2 text-sm rounded-xl border border-slate-700 bg-slate-800 text-white placeholder:text-slate-500 focus:border-violet-500 outline-none";
}
